import { Bot, Braces, FilePlus2, PackageOpen, Search, ShieldAlert } from "lucide-react";
import type { ActivityReceipt } from "../types";
import { formatTokens, timeAgo } from "../utils";
import { Badge } from "./UI";

const iconMap = {
  search: Search,
  retrieve_pack: PackageOpen,
  get_context_pack: PackageOpen,
  get_note: Braces,
  create_note: FilePlus2,
  propose_note: FilePlus2,
  permission_denied: ShieldAlert,
};

export function ActivityRow({ receipt, onClick }: { receipt: ActivityReceipt; onClick?: () => void }) {
  const Icon = iconMap[receipt.action as keyof typeof iconMap] ?? Bot;
  const actions: Record<string, string> = {
    "note.create": "Created a note", "note.update": "Updated a note", "note.archive": "Archived a note",
    "context_pack.create": "Created a context package", "context_pack.update": "Saved a package version",
    "context_pack.assignments.update": "Changed package assignments", "context_pack.request": "Requested a context package",
    "agent.create": "Connected an agent", "agent.update": "Updated an agent", "agent.revoke": "Revoked agent access",
    "memory.feedback": "Recorded knowledge feedback", "automation.run": "Ran an automation",
  };
  const retrieval = receipt.category === "retrieval";
  const description = retrieval
    ? `${receipt.status === "error" ? "Context request failed" : receipt.status === "empty" ? "Context request returned no results" : receipt.status === "ok" ? "Retrieved knowledge" : "Context request recorded"}${receipt.packName ? ` · ${receipt.packName}` : ""}${receipt.query ? ` · “${receipt.query}”` : ""}`
    : `${receipt.status === "error" ? "Failed: " : ""}${actions[receipt.action] ?? receipt.action.replace(/[_.]/g, " ")}${receipt.packName ? ` · ${receipt.packName}` : ""}`;
  const outcome = receipt.status === "ok" ? "Completed" : receipt.status === "empty" ? "No results" : receipt.status === "error" ? "Failed" : receipt.status;
  return (
    <button type="button" className="activity-row" onClick={onClick}>
      <span className={`activity-icon activity-${receipt.status}`}>
        <Icon size={16} />
      </span>
      <span className="activity-main">
        <strong>{receipt.agentName ?? (receipt.category === "system" ? "Lattice" : "Workspace")}</strong>
        <span>{description}</span>
      </span>
      <span className="activity-metrics">
        {receipt.packVersion ? <Badge tone="violet">v{receipt.packVersion}</Badge> : null}
        <Badge tone={receipt.status === "error" ? "coral" : receipt.status === "ok" ? "cyan" : "neutral"}>{outcome}</Badge>
        {retrieval && receipt.status === "ok" ? <code>{formatTokens(receipt.tokens)} tokens</code> : null}
        <time dateTime={receipt.createdAt} title={new Date(receipt.createdAt).toLocaleString()}>{timeAgo(receipt.createdAt)}</time>
      </span>
    </button>
  );
}
